import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import ProfileAction from "./ProfileAction";
import Tab from "./Tab";

import {
  useAuthStore,
  useBusinessUnitStore,
  useCorporationStore,
  useLayoutStore,
} from "@type/store";
import { generateTabItems } from "helpers/generateTabItems";
import { getAllowedTabList } from "helpers/getAllowedTabList";
import { isMatchParentPath } from "helpers/isMatchParentPath";
import { useGetAccess } from "hooks/useGetAccess";
import { useGetMainPathType } from "hooks/useGetMainPathType";
import { useHideTopNavBar } from "hooks/useHideTopNavBar";
import { useRenderStatus } from "hooks/useRenderStatus";
import cn from "lib/cn";

import { ENavTab } from "../types";
import { TAB_LABEL } from "../constants";

import styles from "./Navbar.module.css";

export interface NavbarProps {
  setSidebarCollapsed: Dispatch<SetStateAction<boolean>>;
  id?: string;
}

const Navbar = ({ setSidebarCollapsed, id }: NavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const busy = useRenderStatus(500);

  const { accessType } = useGetAccess({});
  const buAccessRight = useBusinessUnitStore((state) => state.buAccessRight);
  const buAppAccessRight = useBusinessUnitStore(
    (state) => state.buAppAccessRight
  );
  const corpAccessRight = useCorporationStore((state) => state.corpAccessRight);
  const mainPathType = useGetMainPathType();
  const { communitySlug, buSlug, corporationSlug, personalSlug } = useParams();
  const { isHideTopNavBar } = useHideTopNavBar();
  const accessDenied = useLayoutStore((state) => state.accessDenied);
  const isDataNotFound = useLayoutStore((state) => state.isDataNotFound);

  const [selectedTab, setSelectedTab] = useState<ENavTab>(
    ENavTab.GENERAL_SETTING
  );

  const tabItems = useMemo(() => {
    const tabList = getAllowedTabList({
      accessType,
      buAccessRight: {
        ...buAccessRight,
        ...buAppAccessRight,
      },
      corpAccessRight,
      isSuperAdmin: user.isSuperAdmin,
    });

    return generateTabItems({
      communitySlug,
      buSlug,
      corporationSlug,
      personalSlug,
      tabLabel: TAB_LABEL,
      tabList: tabList,
      isSuperAdmin: user.isSuperAdmin,
      pathname: location.pathname,
    });
  }, [
    communitySlug,
    buSlug,
    corporationSlug,
    personalSlug,
    user,
    location.pathname,
    accessType,
    buAccessRight,
    corpAccessRight,
    buAppAccessRight,
  ]);

  const showNavTab = useMemo(() => {
    return !isHideTopNavBar && !accessDenied && !isDataNotFound && !busy;
  }, [isHideTopNavBar, accessDenied, isDataNotFound, busy]);

  const handleClickLogo = () => {
    navigate("/");
    setSidebarCollapsed(true);
  };

  const handleChangeTab = useCallback(
    (tab: string, path?: string) => {
      const targetPath =
        path ??
        tabItems[tab]?.path ??
        Object.values(tabItems[tab]?.children ?? {})[0]?.path;

      if (targetPath) {
        navigate(targetPath);
      }
    },
    [navigate, tabItems]
  );

  useEffect(() => {
    let parentTab: ENavTab | undefined;
    let parentTabLength = 0;

    for (const key in tabItems) {
      const item = tabItems[key];

      if (item.children) {
        for (const child of Object.values(item.children)) {
          if (
            child.path &&
            matchPath({ path: child.path, end: false }, location.pathname)
          ) {
            setSelectedTab(key as ENavTab);
            return;
          }
        }
      }

      if (item.path) {
        if (matchPath({ path: item.path, end: true }, location.pathname)) {
          setSelectedTab(key as ENavTab);
          return;
        }

        const currentArrPath = location.pathname.split("/").filter(Boolean);
        const arrItemPath = item.path.split("/").filter(Boolean);

        if (
          isMatchParentPath({
            currentPaths: currentArrPath,
            entityType: mainPathType,
            parentPath: item.path,
          })
        ) {
          const allSame = arrItemPath.every((item) =>
            currentArrPath.includes(item)
          );

          if (
            (currentArrPath?.[currentArrPath.length - 2] ===
              arrItemPath?.[arrItemPath.length - 1] ||
              allSame) &&
            currentArrPath.length >= parentTabLength
          ) {
            parentTab = key as ENavTab;
            parentTabLength = currentArrPath.length;
          }
        }
      }
    }

    if (parentTab) {
      setSelectedTab(parentTab);
    }
  }, [location.pathname, mainPathType, tabItems]);

  return (
    <div
      data-testid={id}
      className="h-[4rem] flex w-full justify-between items-center gap-6 bg-white pl-24 pr-12 border-b border-line-500"
    >
      <div
        className="w-[8.125rem] min-w-[8.125rem] h-full flex items-center justify-start cursor-pointer border-line-500"
        onClick={handleClickLogo}
      >
        <img alt="logo" src="/images/logo.png" width={60} height={35} />
      </div>
      <div className={cn("h-full", styles.tabWrapper)}>
        {showNavTab && (
          <Tab
            selected={selectedTab}
            onChange={handleChangeTab}
            items={tabItems}
          />
        )}
      </div>
      <div className="w-[30rem] min-w-[7.5rem] flex items-center space-x-6 justify-end">
        <ProfileAction />
      </div>
    </div>
  );
};

export default Navbar;
